/**
 * Разреженное состояние листа: только непустые ячейки и стили.
 */
import { cellKey, shiftFormulaRefs } from './formulaEngine.js';

export const FILL_PRESETS = [
  { id: 'none', label: 'Без заливки', color: null },
  { id: 'yellow', label: 'Жёлтый', color: '#854d0e' },
  { id: 'green', label: 'Зелёный', color: '#14532d' },
  { id: 'blue', label: 'Синий', color: '#1e3a5f' },
  { id: 'red', label: 'Красный', color: '#7f1d1d' },
  { id: 'wall', label: 'Запрет', color: '#334155' },
];

export const VIRTUAL_ROW_THRESHOLD = 80;
export const DEFAULT_ROWS_18 = 20;
export const DEFAULT_COLS_18 = 20;
export const MAX_FULL_ROWS = 50;
export const MAX_FULL_COLS = 50;

export function emptySpreadsheetState(rows = DEFAULT_ROWS_18, cols = DEFAULT_COLS_18) {
  return {
    version: 2,
    rows: Math.max(1, Number(rows) || DEFAULT_ROWS_18),
    cols: Math.max(1, Number(cols) || DEFAULT_COLS_18),
    cells: {},
    fills: {},
    walls: {},
  };
}

/** Старый плотный cells → разреженный; добавить fills/walls. */
export function normalizeSpreadsheetState(raw, fallback) {
  const base = fallback || emptySpreadsheetState();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  let rows = Number(raw.rows) > 0 ? Number(raw.rows) : base.rows;
  let cols = Number(raw.cols) > 0 ? Number(raw.cols) : base.cols;
  const cells = {};
  if (raw.cells && typeof raw.cells === 'object') {
    for (const [k, v] of Object.entries(raw.cells)) {
      if (v == null) continue;
      const s = String(v);
      if (s === '') continue;
      cells[k] = s;
    }
  }
  const fills = {};
  if (raw.fills && typeof raw.fills === 'object') {
    for (const [k, v] of Object.entries(raw.fills)) {
      if (v) fills[k] = String(v);
    }
  }
  const walls = {};
  if (raw.walls && typeof raw.walls === 'object') {
    for (const [k, v] of Object.entries(raw.walls)) {
      if (v && typeof v === 'object') {
        const w = {};
        if (v.t) w.t = 1;
        if (v.r) w.r = 1;
        if (v.b) w.b = 1;
        if (v.l) w.l = 1;
        if (w.t || w.r || w.b || w.l) walls[k] = w;
      }
    }
  }
  // Пустая оболочка старого калькулятора 8×6 → текущие размеры по умолчанию.
  if ((raw.version || 1) < 2 && Object.keys(cells).length === 0) {
    rows = Math.max(rows, fallback.rows || DEFAULT_ROWS_18);
    cols = Math.max(cols, fallback.cols || DEFAULT_COLS_18);
  }
  return {
    version: 2,
    rows,
    cols,
    cells,
    fills,
    walls,
  };
}

export function shouldVirtualize(state, config) {
  if (config?.virtual === true) return true;
  if (config?.virtual === false) return false;
  const rows = state?.rows || 0;
  return rows > VIRTUAL_ROW_THRESHOLD;
}

export function setCellValue(state, r, c, value) {
  const key = cellKey(r, c);
  const cells = { ...(state.cells || {}) };
  const s = value == null ? '' : String(value);
  if (s === '') delete cells[key];
  else cells[key] = s;
  return { ...state, cells };
}

export function setCellsValues(state, updates) {
  const cells = { ...(state.cells || {}) };
  for (const { row, col, value } of updates) {
    const key = cellKey(row, col);
    const s = value == null ? '' : String(value);
    if (s === '') delete cells[key];
    else cells[key] = s;
  }
  return { ...state, cells };
}

export function getFill(state, r, c) {
  return state?.fills?.[cellKey(r, c)] || null;
}

export function getWalls(state, r, c) {
  return state?.walls?.[cellKey(r, c)] || null;
}

export function applyFillToSelection(state, sel, color) {
  if (!sel) return state;
  const fills = { ...(state.fills || {}) };
  const r0 = Math.min(sel.r0, sel.r1);
  const r1 = Math.max(sel.r0, sel.r1);
  const c0 = Math.min(sel.c0, sel.c1);
  const c1 = Math.max(sel.c0, sel.c1);
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) {
      const key = cellKey(r, c);
      if (!color) delete fills[key];
      else fills[key] = color;
    }
  }
  return { ...state, fills };
}

/** Толстые границы на внешнем периметре выделения (стены для ЕГЭ-18). */
export function applyWallsToSelection(state, sel, sides = { t: 1, r: 1, b: 1, l: 1 }) {
  if (!sel) return state;
  const walls = { ...(state.walls || {}) };
  const r0 = Math.min(sel.r0, sel.r1);
  const r1 = Math.max(sel.r0, sel.r1);
  const c0 = Math.min(sel.c0, sel.c1);
  const c1 = Math.max(sel.c0, sel.c1);

  const bump = (r, c, edge) => {
    const key = cellKey(r, c);
    const cur = { ...(walls[key] || {}) };
    if (sides[edge]) cur[edge] = 1;
    else delete cur[edge];
    if (cur.t || cur.r || cur.b || cur.l) walls[key] = cur;
    else delete walls[key];
  };

  for (let c = c0; c <= c1; c += 1) {
    if (sides.t) bump(r0, c, 't');
    if (sides.b) bump(r1, c, 'b');
  }
  for (let r = r0; r <= r1; r += 1) {
    if (sides.l) bump(r, c0, 'l');
    if (sides.r) bump(r, c1, 'r');
  }
  return { ...state, walls };
}

export function clearWallsInSelection(state, sel) {
  if (!sel) return state;
  const walls = { ...(state.walls || {}) };
  const r0 = Math.min(sel.r0, sel.r1);
  const r1 = Math.max(sel.r0, sel.r1);
  const c0 = Math.min(sel.c0, sel.c1);
  const c1 = Math.max(sel.c0, sel.c1);
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) {
      delete walls[cellKey(r, c)];
    }
  }
  return { ...state, walls };
}

export function copyFormat(state, from, toSel) {
  if (!from || !toSel) return state;
  const fill = state.fills?.[cellKey(from.row, from.col)] || null;
  const wall = state.walls?.[cellKey(from.row, from.col)] || null;
  let next = applyFillToSelection(state, toSel, fill);
  const walls = { ...(next.walls || {}) };
  const r0 = Math.min(toSel.r0, toSel.r1);
  const r1 = Math.max(toSel.r0, toSel.r1);
  const c0 = Math.min(toSel.c0, toSel.c1);
  const c1 = Math.max(toSel.c0, toSel.c1);
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) {
      const key = cellKey(r, c);
      if (wall) walls[key] = { ...wall };
      else delete walls[key];
    }
  }
  return { ...next, walls };
}

/**
 * Удалить строки [r0..r1] включительно, сдвинуть ячейки вверх,
 * формулы со ссылками ниже сдвигаем.
 */
export function deleteRows(state, r0, r1) {
  const lo = Math.min(r0, r1);
  const hi = Math.max(r0, r1);
  const count = hi - lo + 1;
  if (count <= 0) return state;
  const newRows = Math.max(1, (state.rows || 1) - count);

  const remapSparse = (map, transformValue) => {
    const next = {};
    for (const [k, v] of Object.entries(map || {})) {
      const [rs, cs] = k.split(',');
      const r = Number(rs);
      const c = Number(cs);
      if (r >= lo && r <= hi) continue;
      const nr = r > hi ? r - count : r;
      if (nr < 0 || nr >= newRows) continue;
      const nv = transformValue ? transformValue(v, r, nr) : v;
      if (nv == null || nv === '') continue;
      next[cellKey(nr, c)] = nv;
    }
    return next;
  };

  const moved = remapSparse(state.cells);
  const cells = {};
  for (const [k, v] of Object.entries(moved)) {
    if (typeof v === 'string' && v.startsWith('=')) {
      cells[k] = shiftRefsAfterRowDelete(v, lo, count);
    } else {
      cells[k] = v;
    }
  }

  return {
    ...state,
    rows: newRows,
    cells,
    fills: remapSparse(state.fills),
    walls: remapSparse(state.walls),
  };
}

/** Сдвинуть ссылки на строки строго ниже удалённого блока. */
export function shiftRefsAfterRowDelete(formula, deletedLo, count) {
  const raw = String(formula || '');
  if (!raw.startsWith('=')) return raw;
  const body = raw.slice(1);
  const re = /(\$?)([A-Za-z]+)(\$?)(\d+)/g;
  const next = body.replace(re, (full, absC, letters, absR, digits, offset) => {
    const before = body[offset - 1];
    if (before && /[A-Za-zА-Яа-яЁё_]/.test(before)) return full;
    const after = body[offset + full.length];
    if (after && /[A-Za-z]/.test(after)) return full;
    const row1 = Number(digits);
    const row0 = row1 - 1;
    if (row0 >= deletedLo && row0 < deletedLo + count) {
      // ссылка в удалённые строки — оставляем как есть (станет «битой»)
      return full;
    }
    if (row0 >= deletedLo + count) {
      const newRow = row0 - count;
      return `${absC}${letters.toUpperCase()}${absR}${newRow + 1}`;
    }
    return full;
  });
  return `=${next}`;
}

/**
 * Протянуть значение/формулу из угла выделения на прямоугольник dest.
 * Источник — активная ячейка (обычно левый верх выделения).
 */
export function fillFormulas(state, source, dest) {
  if (!source || !dest) return state;
  const srcVal = state.cells?.[cellKey(source.row, source.col)] ?? '';
  const updates = [];
  const r0 = Math.min(dest.r0, dest.r1);
  const r1 = Math.max(dest.r0, dest.r1);
  const c0 = Math.min(dest.c0, dest.c1);
  const c1 = Math.max(dest.c0, dest.c1);
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) {
      if (r === source.row && c === source.col) continue;
      const dRow = r - source.row;
      const dCol = c - source.col;
      let value = srcVal;
      if (typeof srcVal === 'string' && srcVal.startsWith('=')) {
        value = shiftFormulaRefs(srcVal, dRow, dCol);
      }
      updates.push({ row: r, col: c, value });
    }
  }
  return setCellsValues(state, updates);
}

export function selectionBounds(sel) {
  if (!sel) return null;
  return {
    r0: Math.min(sel.r0, sel.r1),
    r1: Math.max(sel.r0, sel.r1),
    c0: Math.min(sel.c0, sel.c1),
    c1: Math.max(sel.c0, sel.c1),
  };
}

export function isInSelection(sel, r, c) {
  const b = selectionBounds(sel);
  if (!b) return false;
  return r >= b.r0 && r <= b.r1 && c >= b.c0 && c <= b.c1;
}
